import { Avatar, AvatarGroup } from '@chakra-ui/react';

const MAX_VISIBLE_AVATARS = 3;

export interface SelectedAvatarPerson {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

interface SelectedAvatarsClusterProps {
  /** The selected people, in selection order; only the first few render. */
  people: SelectedAvatarPerson[];
  /**
   * Full selection size. Can exceed `people.length` (select-all-matching),
   * in which case the overflow chip carries the difference.
   */
  totalCount: number;
  /** Avatar diameter as a CSS size. Defaults to the group's `2xs` recipe size. */
  avatarSize?: string;
  /** Horizontal overlap between avatars. Defaults to the action bars' `-1`. */
  overlap?: string;
  /** CSS border shorthand ringing each avatar (e.g. `2px solid #fff`). No ring by default. */
  avatarBorder?: string;
  /** Background behind fallback initials. Defaults to the avatar recipe's. */
  fallbackBg?: string;
}

/**
 * Overlapped avatar cluster: up to three tiny avatars plus a "+N" chip for the
 * rest. Born in the bulk-selection action bars (affiliates table, wishlist),
 * which keep the default styling untouched; the optional style props let other
 * surfaces (program cards) restyle size, overlap, ring, and fallback without
 * forking the slice-and-overflow logic. Renders nothing when `people` is empty.
 */
export const SelectedAvatarsCluster = ({
  people,
  totalCount,
  avatarSize,
  overlap = '-1',
  avatarBorder,
  fallbackBg,
}: SelectedAvatarsClusterProps) => {
  if (people.length === 0) {
    return null;
  }

  const visible = people.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = totalCount - visible.length;
  // All undefined by default, so the action bars render pixel-identical.
  const styleOverrides = { w: avatarSize, h: avatarSize, border: avatarBorder };

  return (
    <AvatarGroup size="2xs" gap="0" spaceX={overlap} borderless>
      {visible.map((person) => (
        <Avatar.Root key={person.id} bg={fallbackBg} {...styleOverrides}>
          <Avatar.Fallback name={person.name} />
          {person.avatarUrl && <Avatar.Image src={person.avatarUrl} />}
        </Avatar.Root>
      ))}
      {overflow > 0 && (
        <Avatar.Root
          variant="subtle"
          colorPalette="rose"
          bg="rose.subtle"
          color="rose.fg"
          {...styleOverrides}
        >
          <Avatar.Fallback>+{overflow}</Avatar.Fallback>
        </Avatar.Root>
      )}
    </AvatarGroup>
  );
};
